/**
 * State shared by the calendar regions (DESIGN.md T13): the rail (mini month, calendar list) lives in the AppShell,
 * the grid and the dialogs on the calendar page. View, date and hidden calendars are per-browser conveniences
 * (localStorage, ignored when unavailable); calendars and events come from the server.
 */
import { useI18n } from '@platform/i18n';
import { BREAKPOINTS, useAsync, useMediaQuery, type AsyncState } from '@platform/ui';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, type CalendarEventDetail, type CalendarEventScope, type CalendarInfo } from '../api';
import type { CalendarLayout, CalendarSettings, CalendarSettingsPatch, Category } from '../api/calendar-extra';
import { useCalendarLayout } from './layout';
import { useCategories } from './categories';
import { zoneShift, type ZoneShift } from './zones';
import { CALENDAR_VIEWS, type CalendarView } from './format';
import { useCalendarSettings } from './settings';

const VIEW_KEY = 'platform.webmail.calendar.view';
const HIDDEN_KEY = 'platform.webmail.calendar.hidden';

function read(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // Not remembered; the choice still applies now.
  }
}

/** Which occurrences an edit changes: one, the whole series, or this and the following ones. */
export type EditScope = CalendarEventScope | 'following';

/** What the event editor opens with. */
export type EditorRequest =
  | {
      mode: 'create';
      start?: Date | undefined;
      /** Exclusive end (a selected range); default: 30 minutes after a given start, else one hour. */
      end?: Date | undefined;
      allDay?: boolean | undefined;
      calendarId?: string | undefined;
      title?: string | undefined;
      /** Context menu "New recurring event" starts with weekly recurrence. */
      recurring?: boolean | undefined;
      attendees?: { address: string; name: string; type: 'required' | 'optional' | 'resource' }[];
    }
  | { mode: 'edit'; event: CalendarEventDetail; scope: EditScope };

/** Dialogs of the calendar rail: sharing of one own calendar, adding someone's calendar, creating/renaming an own one. */
export type CalendarDialog =
  | { kind: 'sharing'; calendar: CalendarInfo }
  | { kind: 'import'; calendar: CalendarInfo }
  | { kind: 'publish'; calendar: CalendarInfo }
  | { kind: 'add' }
  | { kind: 'create' }
  | { kind: 'rename'; calendar: CalendarInfo }
  | { kind: 'bookingRequests' }
  | { kind: 'invitations' };

export interface CalendarContextValue {
  searchText: string;
  setSearchText: (value: string) => void;
  searchQuery: string | undefined;
  setSearchQuery: (value: string | undefined) => void;
  date: Date;
  setDate: (date: Date) => void;
  view: CalendarView;
  setView: (view: CalendarView) => void;
  calendars: AsyncState<CalendarInfo[]>;
  /** Ids of the calendars shown in the grid. */
  visible: ReadonlySet<string>;
  toggleCalendar: (id: string) => void;
  selectedId: string | undefined;
  setSelectedId: (id: string | undefined) => void;
  editor: EditorRequest | undefined;
  openEditor: (request: EditorRequest) => void;
  closeEditor: () => void;
  dialog: CalendarDialog | undefined;
  setDialog: (dialog: CalendarDialog | undefined) => void;
  /** Bumped after every change, so the grid reloads its events. */
  version: number;
  changed: () => void;
  /** The user's calendar settings (server-stored; defaults until loaded). */
  settings: CalendarSettings;
  settingsLoaded: boolean;
  updateSettings: (patch: CalendarSettingsPatch) => void;
  adoptSettings: (patch: CalendarSettingsPatch) => void;
  /** The user's categories and the grid filter by category (empty = all). */
  categories: Category[];
  saveCategories: (categories: readonly Category[]) => void;
  categoryFilter: ReadonlySet<string>;
  toggleCategoryFilter: (name: string) => void;
  /** Display time zone shift (identity when the calendar is shown in the browser's zone). */
  zone: ZoneShift;
  /** Order and groups of the navigation. */
  layout: CalendarLayout;
  saveLayout: (layout: CalendarLayout) => void;
  /** Show only this calendar / all calendars again. */
  showOnly: (id: string) => void;
  showAll: () => void;
}

export const CalendarContext = createContext<CalendarContextValue | null>(null);

/** Calendar state of the shell; `active` = the calendar route is open (nothing is loaded before). */
export function useCalendarState(active: boolean, authenticated = active): CalendarContextValue {
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState<string>();
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const [mountInstant] = useState(() => new Date());
  const [selectedDate, setDate] = useState<Date>();
  const [chosenView, setChosenView] = useState<CalendarView | undefined>(() => {
    const stored = read(VIEW_KEY);
    return (CALENDAR_VIEWS as readonly string[]).includes(stored ?? '') ? (stored as CalendarView) : undefined;
  });
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set((read(HIDDEN_KEY) ?? '').split(',').filter(Boolean)));
  const [selectedId, setSelectedId] = useState<string>();
  const [editor, setEditor] = useState<EditorRequest>();
  const [dialog, setDialog] = useState<CalendarDialog>();
  const [version, setVersion] = useState(0);
  const calendars = useAsync(() => (active ? api.listCalendars() : Promise.resolve([])), active ? 'on' : 'off');
  const { t } = useI18n();
  const { settings, loaded: settingsLoaded, updateSettings, adoptSettings } = useCalendarSettings(authenticated, t('webmail.calendarUx.settings.saveFailed'));
  const zone = useMemo(() => zoneShift(settings.displayTimeZone), [settings.displayTimeZone]);
  // Hydration updates an untouched default day; selected dates and deep links remain wall dates.
  const date = useMemo(() => selectedDate ?? zone.toWall(mountInstant), [selectedDate, zone, mountInstant]);
  useEffect(() => {
    if (active) api.refreshPublications().catch(() => undefined);
  }, [active]);
  const { categories, saveCategories, categoryFilter, toggleCategoryFilter } = useCategories(active, t('webmail.calendarUx.settings.saveFailed'));
  const { layout, saveLayout } = useCalendarLayout(active, t('webmail.calendarUx.settings.saveFailed'));

  // Phones start in the day view, larger screens in the work week (Outlook default).
  const view = chosenView ?? (isDesktop ? 'workWeek' : 'day');
  const setView = useCallback((next: CalendarView) => {
    write(VIEW_KEY, next);
    setChosenView(next);
  }, []);
  const toggleCalendar = useCallback((id: string) => {
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      write(HIDDEN_KEY, [...next].join(','));
      return next;
    });
  }, []);
  const setHiddenIds = useCallback((ids: readonly string[]) => {
    write(HIDDEN_KEY, ids.join(','));
    setHidden(new Set(ids));
  }, []);
  const showOnly = useCallback((id: string) => setHiddenIds((calendars.data ?? []).map((c) => c.id).filter((c) => c !== id)), [calendars.data, setHiddenIds]);
  const showAll = useCallback(() => setHiddenIds([]), [setHiddenIds]);
  const { reload } = calendars;
  const changed = useCallback(() => {
    setVersion((n) => n + 1);
    reload();
  }, [reload]);

  const visible = useMemo(() => new Set((calendars.data ?? []).map((c) => c.id).filter((id) => !hidden.has(id))), [calendars.data, hidden]);

  const value = useMemo<CalendarContextValue>(
    () => ({
      searchText, setSearchText, searchQuery, setSearchQuery,
      date,
      setDate,
      view,
      setView,
      calendars,
      visible,
      toggleCalendar,
      selectedId,
      setSelectedId,
      editor,
      openEditor: setEditor,
      closeEditor: () => setEditor(undefined),
      dialog,
      setDialog,
      version,
      changed,
      settings,
      settingsLoaded,
      updateSettings,
      adoptSettings,
      categories,
      saveCategories,
      categoryFilter,
      toggleCategoryFilter,
      zone,
      layout,
      saveLayout,
      showOnly,
      showAll,
    }),
    [settingsLoaded, searchText, searchQuery, zone, layout, saveLayout, showOnly, showAll, date, view, setView, calendars, visible, toggleCalendar, selectedId, editor, dialog, version, changed, settings, updateSettings, adoptSettings, categories, saveCategories, categoryFilter, toggleCategoryFilter],
  );
  return value;
}

export function useCalendar(): CalendarContextValue {
  const value = useContext(CalendarContext);
  if (!value) throw new Error('useCalendar outside CalendarProvider');
  return value;
}
